import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertOctagon, RefreshCw, Copy, ShieldAlert, Wand2 } from "lucide-react";
import { resetAuthBounces } from "../lib/authLoopBreaker";
import MagicLinkInline from "../components/MagicLinkInline";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Sovereign Diagnostic · /diagnostic (auto-rendered by ProtectedRoute
 * when the auth loop-breaker trips).
 *
 * Fetches `GET /api/auth/diag` and renders the JSON verbatim so the
 * Director can copy/paste it into a support thread. Includes:
 *   • Reset Loop-Counter button (clears the sessionStorage tally so
 *     a user can retry without closing the tab).
 *   • Copy-to-clipboard for the raw JSON.
 *   • Triage checklist (the three known production cookie-loop causes).
 */
export default function SovereignDiagnostic() {
    const [diag, setDiag] = useState(null);
    const [err, setErr] = useState(null);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);

    const load = async () => {
        setLoading(true);
        setErr(null);
        try {
            const res = await fetch(`${BACKEND}/api/auth/diag`, {
                credentials: "include",
                headers: { Accept: "application/json" },
            });
            const json = await res.json().catch(() => ({ _raw_text: "<non-JSON response>" }));
            setDiag({
                http_status: res.status,
                http_ok: res.ok,
                fetched_at: new Date().toISOString(),
                body: json,
            });
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const copyJson = async () => {
        try {
            await navigator.clipboard.writeText(JSON.stringify(diag, null, 2));
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        } catch {
            /* clipboard blocked — user can still select manually */
        }
    };

    const onResetAndRetry = () => {
        resetAuthBounces();
        // Hard navigation so AuthContext also re-runs `fetchMe` from scratch.
        window.location.href = "/login";
    };

    return (
        <div
            data-testid="sovereign-diagnostic"
            className="min-h-screen text-white"
            style={{
                background:
                    "radial-gradient(ellipse at top, #2A0A0A 0%, #0D0510 55%, #050208 100%)",
                fontFamily: "'Inter', sans-serif",
            }}
        >
            {/* Sentinel ribbon */}
            <div className="border-b" style={{ borderColor: "#5A1A1A", background: "rgba(5,2,8,0.8)" }}>
                <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
                    <div
                        className="text-[11px] uppercase tracking-[0.3em] inline-flex items-center gap-2"
                        style={{ color: "#FF6B6B", fontFamily: "'Montserrat', sans-serif", fontWeight: 700 }}
                    >
                        <ShieldAlert size={14} /> Sovereign Diagnostic · Loop-Breaker Engaged
                    </div>
                    <Link
                        to="/"
                        data-testid="diagnostic-home-link"
                        className="text-[11px] uppercase tracking-[0.3em]"
                        style={{
                            color: "#E8C56C",
                            fontFamily: "'Montserrat', sans-serif",
                            fontWeight: 700,
                        }}
                    >
                        ◂ Return to Bridge
                    </Link>
                </div>
            </div>

            <main className="max-w-[1100px] mx-auto px-4 sm:px-6 md:px-10 py-10 sm:py-16">
                <div
                    className="text-[10px] sm:text-[11px] uppercase tracking-[0.35em] sm:tracking-[0.4em] mb-5 sm:mb-6 inline-flex items-center gap-2"
                    style={{
                        color: "#FF6B6B",
                        fontFamily: "'Montserrat', sans-serif",
                        fontWeight: 800,
                    }}
                >
                    <AlertOctagon size={14} />
                    Auth Bounce Threshold Exceeded
                </div>
                <h1
                    className="text-3xl sm:text-4xl md:text-5xl uppercase font-black leading-[0.95] tracking-tighter"
                    style={{ fontFamily: "'Montserrat', sans-serif", color: "#F8FAFC" }}
                    data-testid="diagnostic-h1"
                >
                    The Sovereign Cookie Did Not Stick.
                </h1>
                <p className="mt-5 sm:mt-6 max-w-3xl text-sm sm:text-base md:text-lg leading-relaxed" style={{ color: "#A9BCD2" }}>
                    Your session keeps getting kicked back to <code style={{ color: "#E8C56C" }}>/login</code>{" "}
                    after a successful authentication. The browser isn&rsquo;t holding the access cookie. The raw
                    backend handshake state is below — copy it into a support thread or send to the Director.
                </p>

                {/* Zero-friction recovery panel · Magic-Link fallback.
                    Pinned ABOVE the diagnostic dump so a mobile user
                    (the most likely caller) can immediately request a
                    one-tap login without scrolling past JSON. */}
                <section
                    data-testid="diagnostic-magic-fallback"
                    className="mt-8 sm:mt-10 p-5 sm:p-7"
                    style={{
                        background: "linear-gradient(180deg, rgba(232,197,108,0.08) 0%, rgba(232,197,108,0.02) 100%)",
                        border: "1px solid #E8C56C",
                    }}
                >
                    <div
                        className="text-[10px] sm:text-[11px] uppercase tracking-[0.35em] mb-3 inline-flex items-center gap-2"
                        style={{
                            color: "#E8C56C",
                            fontFamily: "'Montserrat', sans-serif",
                            fontWeight: 800,
                        }}
                    >
                        <Wand2 size={13} /> Zero-Friction Recovery
                    </div>
                    <p className="text-sm leading-relaxed mb-4" style={{ color: "#C2D5EA" }}>
                        Skip the cookie loop entirely. Get a one-tap login link by email — works on any device, no password required.
                    </p>
                    <MagicLinkInline
                        accent="gold"
                        label="Send my magic link"
                        placeholder="commander@yourdomain.com"
                        testidPrefix="diag-magic"
                    />
                </section>

                {/* Diagnostic JSON panel */}
                <section
                    data-testid="diagnostic-panel"
                    className="mt-8 sm:mt-10 relative overflow-hidden"
                    style={{
                        background: "linear-gradient(180deg, #0D1824 0%, #0A1320 100%)",
                        border: "1px solid #E8C56C",
                        boxShadow: "0 0 35px rgba(232,197,108,0.15)",
                    }}
                >
                    <span aria-hidden className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2" style={{ borderColor: "#E8C56C" }} />
                    <span aria-hidden className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2" style={{ borderColor: "#E8C56C" }} />
                    <span aria-hidden className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2" style={{ borderColor: "#E8C56C" }} />
                    <span aria-hidden className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2" style={{ borderColor: "#E8C56C" }} />

                    <div className="px-4 sm:px-6 md:px-8 py-4 sm:py-5 border-b flex items-center justify-between flex-wrap gap-3"
                         style={{ borderColor: "#1E3654" }}>
                        <div
                            className="text-[10px] sm:text-[11px] uppercase tracking-[0.3em] sm:tracking-[0.35em]"
                            style={{
                                color: "#E8C56C",
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 700,
                            }}
                        >
                            ◢ GET /api/auth/diag
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={load}
                                data-testid="diagnostic-refresh"
                                className="inline-flex items-center gap-1.5 px-3 py-2 text-[10px] uppercase tracking-[0.3em] transition-colors"
                                style={{
                                    color: "#A9BCD2",
                                    border: "1px solid #1E3654",
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontWeight: 700,
                                    minHeight: 36,
                                }}
                            >
                                <RefreshCw size={11} className={loading ? "animate-spin" : ""} />
                                {loading ? "Pinging" : "Refresh"}
                            </button>
                            <button
                                onClick={copyJson}
                                data-testid="diagnostic-copy"
                                disabled={!diag}
                                className="inline-flex items-center gap-1.5 px-3 py-2 text-[10px] uppercase tracking-[0.3em] transition-colors disabled:opacity-40"
                                style={{
                                    color: "#E8C56C",
                                    border: "1px solid #E8C56C",
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontWeight: 700,
                                    minHeight: 36,
                                }}
                            >
                                <Copy size={11} />
                                {copied ? "Copied!" : "Copy JSON"}
                            </button>
                        </div>
                    </div>

                    <pre
                        data-testid="diagnostic-json"
                        className="px-4 sm:px-6 md:px-8 py-4 sm:py-5 text-[11px] sm:text-xs md:text-sm leading-relaxed overflow-x-auto"
                        style={{
                            color: "#C2D5EA",
                            fontFamily: "'JetBrains Mono', 'Fira Code', ui-monospace, monospace",
                            whiteSpace: "pre",
                            margin: 0,
                            maxHeight: 380,
                            WebkitOverflowScrolling: "touch",
                        }}
                    >
                        {loading && "Acquiring backend handshake…"}
                        {!loading && err && `// ERROR fetching /api/auth/diag\n${err}`}
                        {!loading && !err && diag && JSON.stringify(diag, null, 2)}
                    </pre>
                </section>

                {/* Triage checklist */}
                <section
                    data-testid="diagnostic-triage"
                    className="mt-8 sm:mt-10 p-5 sm:p-6 md:p-8"
                    style={{
                        background: "rgba(5,10,15,0.55)",
                        border: "1px solid #1E3654",
                    }}
                >
                    <div
                        className="text-[10px] sm:text-[11px] uppercase tracking-[0.3em] sm:tracking-[0.35em] mb-4"
                        style={{
                            color: "#00E5FF",
                            fontFamily: "'Montserrat', sans-serif",
                            fontWeight: 800,
                        }}
                    >
                        ◣ Triage Checklist · Known Root Causes
                    </div>
                    <ol className="space-y-3 text-xs sm:text-sm md:text-base leading-relaxed" style={{ color: "#C2D5EA" }}>
                        <li>
                            <strong style={{ color: "#E8C56C" }}>1. Cookie domain mismatch.</strong>{" "}
                            If <code className="break-all">cookie_domain_effective</code> above is{" "}
                            <code style={{ color: "#FF6B6B" }} className="break-all">.bizboogie.com</code> while you&rsquo;re on{" "}
                            <code className="break-all">eigooinc.net</code>, the browser drops every Set-Cookie. Fix:
                            unset the production <code className="break-all">COOKIE_DOMAIN</code> env var, or set it explicitly
                            to <code className="break-all">.eigooinc.net</code>.
                        </li>
                        <li>
                            <strong style={{ color: "#E8C56C" }}>2. Cookie not stored after login.</strong>{" "}
                            If <code className="break-all">has_access_cookie</code> is{" "}
                            <code style={{ color: "#FF6B6B" }}>false</code> immediately after a successful{" "}
                            <code className="break-all">POST /api/auth/login</code>, browser rejected the Set-Cookie. Usually
                            caused by Cloudflare &ldquo;Flexible SSL&rdquo; mode — switch CF to{" "}
                            <strong>Full (strict)</strong>.
                        </li>
                        <li>
                            <strong style={{ color: "#E8C56C" }}>3. Cross-origin mismatch.</strong>{" "}
                            If <code className="break-all">request_origin</code> differs from <code className="break-all">request_host</code>, your
                            frontend is talking to the backend from a domain that isn&rsquo;t in the CORS
                            allowlist with <code className="break-all">credentials: true</code>. Append the origin to{" "}
                            <code className="break-all">CORS_ORIGINS</code> and redeploy backend.
                        </li>
                    </ol>
                </section>

                {/* Actions · stack vertically on mobile, side-by-side on desktop */}
                <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4">
                    <button
                        onClick={onResetAndRetry}
                        data-testid="diagnostic-reset-retry"
                        className="inline-flex items-center justify-center gap-2 px-6 sm:px-7 py-3.5 uppercase tracking-[0.22em] text-xs sm:text-sm font-bold transition-all duration-300"
                        style={{
                            fontFamily: "'Montserrat', sans-serif",
                            background: "#E8C56C",
                            color: "#050A0F",
                            border: "1px solid #E8C56C",
                            minHeight: 48,
                        }}
                    >
                        [ RESET COUNTER · RETRY LOGIN ]
                    </button>
                    <Link
                        to="/"
                        data-testid="diagnostic-back"
                        className="inline-flex items-center justify-center gap-2 px-6 sm:px-7 py-3.5 uppercase tracking-[0.22em] text-xs sm:text-sm font-bold transition-all duration-300"
                        style={{
                            fontFamily: "'Montserrat', sans-serif",
                            color: "#00E5FF",
                            border: "1px solid #00E5FF",
                            background: "transparent",
                            minHeight: 48,
                        }}
                    >
                        [ RETURN TO HUB ]
                    </Link>
                </div>
            </main>

            <div className="h-12" />
        </div>
    );
}
